/* SOLVEX accessibility menu (sections/solvex-accessibility.liquid).
 *
 * State is one object in localStorage under `solvex-a11y`:
 *   { text: 0-3, contrast, grayscale, links, font, spacing, motion, cursor }
 * Each flag maps to a class on <html>; the stylesheet does the rest. The inline
 * script in layout/theme.liquid applies the same classes before first paint and
 * must stay in step with CLASSES and TEXT_STEPS below.
 */
(function () {
  if (customElements.get('solvex-accessibility')) return;

  var STORAGE_KEY = 'solvex-a11y';
  var TEXT_SCALE = [100, 115, 130, 150];
  var CLASSES = {
    contrast: 'sx-a11y-contrast',
    grayscale: 'sx-a11y-grayscale',
    links: 'sx-a11y-links',
    font: 'sx-a11y-font',
    spacing: 'sx-a11y-spacing',
    motion: 'sx-a11y-stop-motion',
    cursor: 'sx-a11y-cursor',
  };

  function readState() {
    try {
      var saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
      return saved && typeof saved === 'object' ? saved : {};
    } catch (error) {
      return {};
    }
  }

  function writeState(state) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // Storage blocked: the choice still applies to this page view.
    }
  }

  function applyState(state) {
    var root = document.documentElement;
    Object.keys(CLASSES).forEach(function (flag) {
      root.classList.toggle(CLASSES[flag], Boolean(state[flag]));
    });
    for (var step = 1; step < TEXT_SCALE.length; step++) {
      root.classList.toggle('sx-a11y-text-' + step, state.text === step);
    }
  }

  // Dawn's slideshow has its own pause button; press it rather than reaching into
  // its timers, so its button label and state stay truthful.
  function pauseSlideshows() {
    document.querySelectorAll('slideshow-component').forEach(function (slideshow) {
      if (slideshow.sliderAutoplayButton && slideshow.autoplayButtonIsSetToPlay) {
        slideshow.autoPlayToggle();
      }
    });
  }

  class SolvexAccessibility extends HTMLElement {
    connectedCallback() {
      // connectedCallback fires again whenever the element is moved in the DOM.
      // Binding twice would make every click toggle an option on and straight off.
      if (this.initialised) return;
      this.initialised = true;

      this.state = readState();
      this.launcher = this.querySelector('[data-sx-a11y-open]');
      this.panel = this.querySelector('.sx-a11y__panel');
      this.textValue = this.querySelector('[data-sx-a11y-text-value]');
      this.textButtons = this.querySelectorAll('[data-sx-a11y-text]');
      this.toggles = this.querySelectorAll('[data-sx-a11y-toggle]');
      if (!this.launcher || !this.panel) return;

      this.onKeydown = this.onKeydown.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);

      this.launcher.addEventListener('click', () => (this.panel.hidden ? this.open() : this.close()));
      this.querySelector('[data-sx-a11y-close]').addEventListener('click', () => this.close());
      this.querySelector('[data-sx-a11y-reset]').addEventListener('click', () => this.reset());

      this.textButtons.forEach((button) => {
        button.addEventListener('click', () => this.changeText(Number(button.dataset.sxA11yText)));
      });

      this.toggles.forEach((button) => {
        button.addEventListener('click', () => this.toggle(button.dataset.sxA11yToggle));
      });

      applyState(this.state);
      this.render();

      if (this.state.motion) {
        if (document.readyState === 'complete') pauseSlideshows();
        else window.addEventListener('load', pauseSlideshows, { once: true });
      }
    }

    open() {
      this.panel.hidden = false;
      this.launcher.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('pointerdown', this.onPointerDown);
      var first = this.panel.querySelector('.sx-a11y__close');
      if (typeof window.trapFocus === 'function') window.trapFocus(this.panel, first);
      else first.focus();
    }

    close() {
      if (this.panel.hidden) return;
      this.panel.hidden = true;
      this.launcher.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('pointerdown', this.onPointerDown);
      if (typeof window.removeTrapFocus === 'function') window.removeTrapFocus(this.launcher);
      else this.launcher.focus();
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }

    onPointerDown(event) {
      if (!this.contains(event.target)) this.close();
    }

    toggle(flag) {
      if (!CLASSES[flag]) return;
      this.state[flag] = !this.state[flag];
      this.commit(flag === 'motion');
    }

    changeText(delta) {
      var next = Math.min(Math.max((this.state.text || 0) + delta, 0), TEXT_SCALE.length - 1);
      if (next === (this.state.text || 0)) return;
      this.state.text = next;
      this.commit(false);
    }

    reset() {
      var motionWasOn = Boolean(this.state.motion);
      this.state = {};
      this.commit(motionWasOn);
    }

    commit(motionChanged) {
      writeState(this.state);
      applyState(this.state);
      this.render();
      if (motionChanged) {
        if (this.state.motion) pauseSlideshows();
        document.dispatchEvent(new CustomEvent('solvex:stop-motion', { detail: { stopped: Boolean(this.state.motion) } }));
      }
    }

    render() {
      var step = this.state.text || 0;
      if (this.textValue) this.textValue.textContent = TEXT_SCALE[step] + '%';
      this.textButtons.forEach(function (button) {
        var atLimit = Number(button.dataset.sxA11yText) < 0 ? step === 0 : step === TEXT_SCALE.length - 1;
        // aria-disabled, not disabled: a disabled button drops keyboard focus mid-use.
        button.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
      });
      this.toggles.forEach((button) => {
        button.setAttribute('aria-pressed', this.state[button.dataset.sxA11yToggle] ? 'true' : 'false');
      });
      var anyOn = Boolean(step) || Object.keys(CLASSES).some((flag) => this.state[flag]);
      this.classList.toggle('has-settings', anyOn);
    }
  }

  customElements.define('solvex-accessibility', SolvexAccessibility);
})();
